import { AprType, DepositsOfferProductSubType, LoanOfferProductSubType } from 'embed/constants'
import { DepositProductTypesOptions, MortgageLoanType, TermUnit } from 'even-ts-static'
export interface CreditPartner {
  imageUrl?: string
  name?: string
  description?: string
  disclaimer?: string
  shouldDisplayPreSelect?: boolean
  subtextOverride?: string
  supportsPersonalizedOffers?: boolean
  supportsPreSelect?: boolean
  uuid?: string
}

export interface CreditOfferEarningProgram {
  cardLevelEarningScheme: CardLevelEarningScheme[]
  personalization?: EarningItemPersonalization
  transactionLevelEarningScheme: TransactionLevelEarningScheme[]
}

interface CardLevelEarningScheme {
  bonus: string
  name: CardLevelEarningName
  personalization?: BonusPersonalization
  redemptionValue: string
  requirement: string
  type: CardLevelEarningType
}

interface BonusPersonalization {
  minimumSpendMet?: boolean
  qualifyingSpend?: string
}

enum CardLevelEarningType {
  ONE_TIME = 'One Time',
  RECURRING = 'Recurring',
}

enum CardLevelEarningName {
  ANNIVERSARY_BONUS = 'Anniversary Bonus',
  ANNUAL_BONUS = 'Annual Bonus',
  SIGN_UP_BONUS = 'Sign-up Bonus',
  USAGE_BONUS = 'Usage Bonus',
}

export enum Currency {
  USD = 'USD',
}

export interface RewardsValueCalculationRequestBody {
  rateTableUuid: string
  aggregates?: RewardsValueAggregate[]
  preferredRedemptionTypes?: RedemptionType[]
}

export interface RewardsValueAggregate {
  amount: number
  uuid: string
}

export interface RewardValueCalculationResponse {
  uuid: string
  rateTableUuid: string
  offerCalculations: OfferCalculation[]
  createdAt: string
  createdBy: string
  spendingProportions?: SpendingProportion[]
  preferredRedemptionTypes?: RedemptionType[]
}

export interface RedemptionType {
  redemptionTypeUuid: string
  weight: number
}

interface SpendingProportion {
  categoryUuid: string
  proportion: number
}
interface OfferCalculation {
  staticOfferUuid: string
  earningProgram: CreditOfferEarningProgram
}

interface TransactionLevelEarningScheme {
  detail?: string
  explaination?: string
  groups: TransactionLevelEarningSchemeGroup[]
  personalization?: EarningItemPersonalization
  type: string
}

interface TransactionLevelEarningSchemeGroup {
  earnRate: number
  earnRateText: string
  items: CategoryEarningItem[] | MerchantEarningItem[]
  personalization: EarningItemPersonalization
}

interface CategoryEarningItem {
  category: EarningProgramCategory
}

interface MerchantEarningItem {
  merchant: Merchant
}

interface EarningProgramCategory {
  displayName?: string
  name?: string
  phrase?: string
  uuid?: string
}

interface Merchant {
  category: EarningProgramCategory
  logo: string
  name: string
  uuid: string
}

interface EarningItemPersonalization {
  totalEarned?: number
  totalSpent?: number
  totalValue?: number
}

export interface CreditOfferDetails {
  additionalDetails?: string[]
  annualIntroFee?: number
  annualIntroFeeTerm?: number
  annualFee?: number
  aprType?: AprType
  cardImageUrl?: string
  cardName?: string
  cardPurposes?: string[]
  cashAdvanceIntroAprTerm?: number
  cashAdvanceIntroAprTermUnits?: string
  details?: string[]
  earningProgram?: CreditOfferEarningProgram
  foreignExchangeFee?: number
  introOfferAmount?: number
  introOfferText?: string
  introOfferType?: string
  maxAnnualFee?: number
  maxBalanceTransferApr?: number
  maxBalanceTransferIntroApr?: number
  maxCashAdvanceApr?: number
  maxCashAdvanceIntroApr?: number
  maximumPenaltyApr?: number
  maxPurchaseApr?: number
  maxPurchaseIntroApr?: number
  minAnnualFee?: number
  minBalanceTransferApr?: number
  minBalanceTransferIntroApr?: number
  minCashAdvanceApr?: number
  minCashAdvanceIntroApr?: number
  minimumPenaltyApr?: number
  minPurchaseApr?: number
  minPurchaseIntroApr?: number
  preApproved?: boolean
  preQualified?: boolean
  preSelected?: boolean
  purchaseAprText?: string
  purchaseIntroAprText?: string
  purchaseIntroAprTerm?: number
  purchaseIntroAprTermUnit?: string
  ratesUrl?: string
  recommendedCreditRatings?: string[]
}

export interface CreditOfferMarketplace {
  description: string
  disclaimer: string
  imageUrl: string
  name: string
  shouldDisplayPreSelect?: boolean
  supportsPersonalizedOffers: boolean
  supportsPreSelect?: boolean
  uuid: string
}

export interface CreditOffer {
  details: CreditOfferDetails
  marketplace?: CreditOfferMarketplace
  partner?: CreditPartner
  productSubType?: string
  productType?: string
  recommendationScore?: number
  uuid: string
  url: string
}

export interface DepositsPartner {
  description?: string
  disclaimer?: string
  imageUrl?: string
  name?: string
  supportsPersonalizedOffers?: boolean
  uuid?: string
}

export interface DepositsOffer {
  details: DepositsOfferDetails
  marketplace?: DepositsPartner
  partner?: DepositsPartner
  productSubType?: DepositsOfferProductSubType | DepositProductTypesOptions
  recommendationScore?: number
  url: string
  uuid: string
}

export interface DepositsOfferDetails {
  annualPercentYield: number
  checkWriting: boolean
  compoundingMethod: string
  description: string
  effectiveAsOf: string
  introductoryPeriodMonths: number
  introductoryRate: number
  minimumDeposit: number
  minimumDepositWithFees: number
  monthlyFee: number
  name: string
  rate: number
}

export interface LifeInsuranceOffer {
  details: LifeInsuranceOfferDetails
  marketplace?: LifeInsurancePartner
  partner: LifeInsurancePartner
  productSubType: LifeInsuranceProductSubType
  productSubTypeDisclaimer?: string
  productType: string
  recommendationScore?: number
  url: string
  uuid: string
  disclaimer?: string
}

export enum LifeInsuranceProductSubType {
  AccidentalDeathBenefits = 'accidental_death_benefits',
  TermLife = 'term_life',
  WholeLife = 'whole_life',
}

export interface LifeInsurancePartner {
  amBestRating?: string
  description?: string
  disclaimer?: string
  foundedYear?: number
  imageUrl: string
  name: string
  shouldDisplayPreSelect?: boolean
  supportsPersonalizedOffers?: boolean
  supportsPreSelect?: boolean
  uuid: string
  headquarters?: string
  assets?: string
  subtextOverride?: string
}
export enum LifeInsuranceMedicalExam {
  NoExamIfQualified = 'no_exam_if_qualified',
  NoExamGuaranteed = 'no_exam_guaranteed',
  ExamRequired = 'exam_required',
}

export const LifeInsuranceMedicalExamLabels: { [key in LifeInsuranceMedicalExam]: string } = {
  [LifeInsuranceMedicalExam.ExamRequired]: 'Medical Exam Required',
  [LifeInsuranceMedicalExam.NoExamGuaranteed]: 'No Medical Exam Guaranteed',
  [LifeInsuranceMedicalExam.NoExamIfQualified]: 'No Medical Exam If You Qualify',
}

export const LifeInsuranceADBLabel = 'Accidental Coverage Only'

export interface LifeInsuranceOfferDetails {
  policyAmount: number
  monthlyPremiumAmount: number
  annualPremiumAmount?: number
  semiAnnualPremiumAmount?: number
  policyName?: string
  carrierDecisionType?: string
  termLength?: number
  termUnit?: LifeInsuranceOfferTermUnit
  onlineAppAvailable?: boolean
  noMedicalTest?: boolean
  recommended?: boolean
  packageType?: string
  partnerQuoteId?: string
  convertToPermanent?: boolean
  childRider?: string
  deliveryMethod?: string
  examRequired?: string
  medicalExam: LifeInsuranceMedicalExam
}

export enum LifeInsuranceOfferTermUnit {
  DAY = 'day',
  MONTH = 'month',
  YEAR = 'year',
}

export interface LoanOriginatorImages {
  sizeKey?: string
  url?: string
}

export interface LoanOriginator {
  name?: string
  description?: string
  disclaimer?: string
  images?: LoanOriginatorImages[]
  key?: string
  bbbRating?: string
  companyUuid: string
}

export interface LoanOffer {
  allowPrepayment?: boolean
  aprType?: AprType
  coApplicant?: boolean
  conversionProbability?: number
  feeFixed?: number
  feeRate?: number
  maxAmount?: number
  maxApr?: number
  maxFeeFixed?: number
  maxFeeRate?: number
  maxMonthlyPayment?: number
  maxTotalPayment?: number
  meanApr?: number
  meanMonthlyPayment?: number
  meanTotalPayment?: number
  minAmount?: number
  minApr?: number
  minFeeFixed?: number
  minFeeRate?: number
  minMonthlyPayment?: number
  minTotalPayment?: number
  monthlyPayment?: number
  originator?: LoanOriginator
  originatorId?: string
  payout?: number
  preApproved: boolean
  preQualified: boolean
  prepaymentFee?: number
  productSubType: LoanOfferProductSubType
  productType?: string
  recommendationScore?: number
  secured?: boolean
  sponsored?: boolean
  termLength: number
  termUnit: TermUnit
  terms?: string
  url: string
  uuid: string
  productSubTypeDisclaimer?: string
  aprDescription?: string
  monthlyPaymentDescription?: string
  termDescription?: string
}

export interface MortgageOffer {
  details: MortgageOfferDetails
  disclaimer: string
  partner: MortgageOfferPartner
  url: string
  uuid: string
}

export interface MortgageOfferDetails {
  adjustmentType: string
  apr: number
  interestRate: number
  loanTerm: number
  loanType: MortgageLoanType
  monthlyPayment: number
  netClosingCosts: number
  priceAdjustment: number
}

export interface MortgageOfferPartner {
  description?: string
  disclaimer?: string
  imageUrl: string
  name: string
  uuid: string
}

export interface SpecialOffer {
  uuid: string
  name: string
  desc: string
  url: string
  partnerName: string
  partnerImageUrl: string
  recommendationScore: number
}

// e.g. the model returned from calling `/originator/staticLoanOffers/:id`
export interface OfferDetail {
  createdAt: number
  createdByUuid: string
  creditCard?: CreditOffer
  depositsOffer?: DepositsOffer
  id: number
  loanOffer?: LoanOffer
  mortgageOffer?: MortgageOffer
  name: string
}

export type Offer = CreditOffer | DepositsOffer | LifeInsuranceOffer | LoanOffer | MortgageOffer | SpecialOffer
